import { logger } from '@tunasong/models'
import type { BandSpace, Persisted, Profile, ProfileGear, Rider, RiderMember } from '@tunasong/schemas'

/**
 * Extract gear config from a profile's config object
 */
function getProfileGear(profile: Profile): ProfileGear | undefined {
  const config = profile.config as Record<string, unknown> | undefined
  const gear = config?.gear as ProfileGear | undefined

  // Log for debugging
  if (!gear) {
    logger.debug('Profile has no gear config', { userId: profile.userId })
  } else if (!gear.requirements || gear.requirements.length === 0) {
    logger.debug('Profile gear has no requirements', { userId: profile.userId, gear })
  } else {
    logger.debug('Profile gear loaded', {
      userId: profile.userId,
      requirementCount: gear.requirements.length,
      requirements: gear.requirements,
    })
  }

  return gear
}

/**
 * Generate a rider from bandspace member profiles
 */
export function generateRiderFromProfiles(
  bandspace: Persisted<BandSpace>,
  memberProfiles: Profile[]
): Partial<Rider> {
  // Map member profiles to rider members
  const members: RiderMember[] = memberProfiles.map((profile, index) => {
    const gear = getProfileGear(profile)
    return {
      userId: profile.userId,
      role: gear?.role,
      requirements: gear?.requirements ?? [],
      channelNumber: index + 1,
      monitorMix: index + 1,
    }
  })

  // Aggregate monitor needs from member preferences
  const monitorNeeds = memberProfiles.reduce(
    (acc, profile) => {
      const gear = getProfileGear(profile)
      const pref = gear?.monitorPreference
      if (pref === 'wedge' || pref === 'both') {
        acc.wedges = (acc.wedges ?? 0) + 1
      }
      if (pref === 'iem' || pref === 'both') {
        acc.iemPacks = (acc.iemPacks ?? 0) + 1
      }
      return acc
    },
    { wedges: 0, iemPacks: 0, mixes: memberProfiles.length }
  )

  return {
    type: 'rider',
    name: `${bandspace.name ?? 'Band'} Technical Rider`,
    parentId: bandspace.id,
    members,
    shared: {
      monitors: monitorNeeds,
    },
    generatedFromProfiles: true,
  }
}
